import {
  addMinutes,
  format,
  isAfter,
  isBefore,
  isWithinInterval,
  parse,
} from "date-fns";
import { useEffect, useState } from "react";
import {
  Button,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
} from "../../../components";
import { InputErros } from "../../../helpers";
import {
  AdzapService,
  AgendamentoServicosService,
  MensagemPersonalizadaService,
  notification,
  PessoaService,
  ServicoService,
  SessionService,
} from "../../../services";
import { formatDate, responseErros } from "../../../utils";
import AgendamentoServicosValidator from "../../servicos/agendamento/middlewares/agendamento-servicos.validator";
import AgendamentoServicoForm from "./agendamento.component";

const AgendamentoServicoDialog = ({
  open,
  setOpen,
  data,
  atualizarStatus,
  setLoading,
  horariosFuncionamento,
}) => {
  const [agendamento, setAgendamento] = useState({});
  const [clienteList, setClienteList] = useState([]);
  const [servicoList, setServicoList] = useState([]);
  const [prestadorServicoList, setPrestadorServicoList] = useState([]);
  const [horariosList, setHorariosList] = useState([]);
  const [servico, setServico] = useState(null);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const pessoaService = new PessoaService();
  const servicoService = new ServicoService();
  const agendamentoServicosService = new AgendamentoServicosService();
  const mensagemPersonalizadaService = new MensagemPersonalizadaService();
  const adzapService = new AdzapService();
  const sessionService = new SessionService();
  const empresaUsuario = sessionService.getEmpresaAndUsuario();
  const configuracoes = empresaUsuario.empresa.configuracoes;

  function onCloseDialog() {
    setAgendamento({});
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      const cliente = {
        clienteNome: data?.cliente,
        id: data?.clienteId,
      };
      const servico = {
        id: data?.servicoId,
        servicoNome: data?.servico,
      };
      setClienteList([cliente]);
      setServicoList([servico]);

      const agendamento = {
        clienteId: data?.clienteId,
        clienteNome: data?.cliente,
        celular: data?.celular,
        servicoId: data?.servicoId,
        servicoNome: data?.servico,
        prestadorServicoId: data?.prestadorServicoId,
        data: formatDate.toSend(new Date()),
      };
      setAgendamento(agendamento);
      buscarServico(data?.servicoId);
      buscarPrestadoresServico();
    }
  }, [open]);

  function formatarHorariosDisponiveis(horariosAgendados, dataSelecionada) {
    const diaSemana = dataSelecionada.getDay(),
      intervalo = configuracoes.intervaloAgendamentos;
    const intervalosFuncionamento = horariosFuncionamento.find(
      (item) => diaSemana === item.diaSemana,
    ).intervalos;

    const horarios = [];
    for (const horario of intervalosFuncionamento) {
      let horaInicial = parse(horario.abertura, "HH:mm:ss", new Date()),
        horaFechamento = parse(horario.fechamento, "HH:mm:ss", new Date());

      while (horaInicial < horaFechamento) {
        const horarioString = format(horaInicial, "HH:mm");
        if (addMinutes(horaInicial, intervalo) <= horaFechamento) {
          if (!horariosAgendados?.includes(horarioString)) {
            horarios.push(horarioString);
          }
        }
        horaInicial = addMinutes(horaInicial, intervalo);
      }
    }
    return horarios;
  }

  function formatarHorariosAgendados(horariosAgendados) {
    const intervalo = configuracoes.intervaloAgendamentos;

    return horariosAgendados?.reduce((acc, curr) => {
      if (!curr.tempoServico) return [...acc, curr.horario];
      const proporcao = curr.tempoServico / intervalo;
      const horarios = [curr.horario];
      let dataAtual = parse(curr.horario, "HH:mm", new Date());

      for (let i = 1; i < proporcao; i++) {
        dataAtual = addMinutes(dataAtual, intervalo);
        const horarioFormatado = format(dataAtual, "HH:mm");
        horarios.push(horarioFormatado);
      }

      return [...acc, ...horarios];
    }, []);
  }

  function filtrarHorariosDisponiveis(horariosAgendados, dataSelecionada) {
    const agendaFormatada = formatarHorariosAgendados(horariosAgendados);
    const horarios = formatarHorariosDisponiveis(
      agendaFormatada,
      dataSelecionada,
    );
    if (!horarios.length) {
      notification.alertaGenericos("Não há horários disponíveis para este dia");
    }
    return horarios;
  }

  function validarHorariosDisponiveis(
    tempoServico,
    tempoAgendamento,
    horarios,
  ) {
    const resultados = [],
      proporcao = tempoServico / tempoAgendamento;
    for (let i = 0; i < horarios.length; i++) {
      const atual = horarios[i];
      let success = true,
        atualTemp = parse(atual, "HH:mm", new Date());

      for (let j = 1, k = i + 1; j < proporcao; j++, k++) {
        atualTemp = addMinutes(atualTemp, tempoAgendamento);
        const dataFormatada = format(atualTemp, "HH:mm");
        const proximo = horarios[k];
        if (dataFormatada !== proximo) {
          success = false;
          break;
        }
      }

      if (success) resultados.push(atual);
    }

    return resultados;
  }

  const buscarAgendamentosDia = async (
    filtros,
    dataSelecionada,
    tempoExecucao = null,
  ) => {
    setLoading(true);

    const tempoServico = tempoExecucao ?? servico.tempoExecucao;
    const result = await agendamentoServicosService.getAllAgendamentosDia(
      filtros,
    );

    if (!result.isAxiosError) {
      const horarios = filtrarHorariosDisponiveis(result.data, dataSelecionada);
      const resultados = validarHorariosDisponiveis(
        tempoServico,
        configuracoes.intervaloAgendamentos,
        horarios,
      );
      if (!resultados.length) {
        notification.alertaGenericos("Não há horários disponíveis neste dia");
        setHorariosList([]);
        setLoading(false);
        return;
      }
      setLoading(false);
      setHorariosList(resultados);
    } else {
      responseErros(result);
      setLoading(false);
      return [];
    }
  };

  const buscarServico = async (id) => {
    const result = await servicoService.getById(id);
    if (!result.isAxiosError) {
      setServico(result.data);
      if (configuracoes.intervaloAgendamentos) {
        const filtroTemp = {
          prestadorServicoId: data?.prestadorServicoId,
          diaReferencia: formatDate.toSend(new Date()),
        };
        buscarAgendamentosDia(
          filtroTemp,
          new Date(),
          result.data.tempoExecucao,
        );
      }
    } else {
      responseErros(result);
    }
  };

  const buscarPrestadoresServico = async (filtros) => {
    const filtro = {
      ...filtros,
      tiposPessoaId: 5,
      nonPaginated: true,
      ativado: true,
    };

    const pessoas = await buscarPessoas(filtro);
    setPrestadorServicoList(pessoas);
  };

  async function buscarPessoas(filtros) {
    const res = await pessoaService.getAll(filtros);
    if (!res.isAxiosError) {
      return res.data;
    } else {
      responseErros(res);
    }
    return [];
  }

  const atualizarHorariosDisponiveis = (dataReferencia, prestadorServicoId) => {
    if (configuracoes.intervaloAgendamentos) {
      const filtroTemp = {
        diaReferencia: formatDate.toSend(dataReferencia),
        prestadorServicoId,
      };
      buscarAgendamentosDia(filtroTemp, dataReferencia, servico.tempoExecucao);
    }
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }

    if (date !== "Invalid Date") {
      const diasFuncionamento = configuracoes.diasSemanaFuncionamento;
      if (diasFuncionamento?.includes(date.getDay())) {
        atualizarHorariosDisponiveis(date, agendamento.prestadorServicoId);
      } else if (diasFuncionamento) {
        notification.alertaGenericos("Este dia da semana não está disponível");
      }
    }

    setAgendamento((prev) => ({
      ...prev,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : "",
    }));
  };

  const onChangePrestador = (value) => {
    atualizarHorariosDisponiveis(new Date(agendamento?.data), value?.id);
    setAgendamento((prev) => ({
      ...prev,
      prestadorServicoId: value?.id,
    }));
  };

  function validarHorarioFuncionamento(time) {
    const intervalosFuncionamento =
      horariosFuncionamento.find(
        (item) => new Date(agendamento?.data).getDay() === item.diaSemana,
      )?.intervalos ?? [];

    for (const intervalo of intervalosFuncionamento) {
      const abertura = parse(intervalo.abertura, "HH:mm:ss", new Date()),
        fechamento = parse(intervalo.fechamento, "HH:mm:ss", new Date());

      if (servico) {
        const terminoServico = addMinutes(time, servico.tempoExecucao);
        if (
          !(isBefore(time, abertura) || isAfter(terminoServico, fechamento))
        ) {
          return true;
        }
      } else if (!(isBefore(time, abertura) || isAfter(time, fechamento))) {
        return true;
      }
    }

    return false;
  }

  async function validarHorarioManual() {
    const horaString = format(new Date(agendamento.periodo), "HH:mm");
    const time = parse(horaString, "HH:mm", new Date());

    const validacao = validarHorarioFuncionamento(time);
    if (!validacao) return false;

    const filtroTemp = {
      diaReferencia: agendamento.periodo,
      prestadorServicoId: agendamento.prestadorServicoId,
    };
    const result = await agendamentoServicosService.getAllAgendamentosDia(
      filtroTemp,
    );

    if (!result.isAxiosError) {
      const agendamentosPrestador = result.data;
      for (const horarioPrestador of agendamentosPrestador) {
        const start = parse(horarioPrestador.horario, "HH:mm", new Date()),
          end = addMinutes(start, horarioPrestador.tempoServico ?? 0);

        if (isWithinInterval(time, { start, end })) {
          return false;
        }

        if (servico) {
          const terminoServico = addMinutes(time, servico.tempoExecucao);
          if (isBefore(start, terminoServico) && isBefore(time, end)) {
            return false;
          }
        }
      }
    } else {
      responseErros(result);
      return false;
    }

    return true;
  }

  const handleSubmit = async () => {
    setLoading(true);
    if (!agendamento.periodo) {
      notification.alertaGenericos("Selecione um horário válido");
      setLoading(false);
      return;
    }

    const diasFuncionamento = configuracoes.diasSemanaFuncionamento,
      dataSelecionada = agendamento?.data;
    if (diasFuncionamento && dataSelecionada) {
      if (!diasFuncionamento.includes(new Date(dataSelecionada).getDay())) {
        notification.alertaGenericos("Este dia da semana não está disponível");
        setLoading(false);
        return;
      }
    }

    if (!configuracoes.intervaloAgendamentos && horariosFuncionamento.length) {
      const validacao = await validarHorarioManual();
      if (!validacao) {
        setLoading(false);
        notification.alertaGenericos("Horário indisponível");
        return;
      }
    }

    const agendamentoTemp = {
      ...agendamento,
      tempoServico: servico.tempoExecucao,
      chaveAtivo: data.chaveAtivo,
    };
    AgendamentoServicosValidator.validate(agendamentoTemp, {
      abortEarly: false,
    })
      .then(async () => {
        let mensagemLembrete = null;
        const empresa = empresaUsuario?.empresa;
        if (
          (empresa?.whatsappSimples || empresa?.whatsappAvancado) &&
          empresa?.configuracoes?.lembreteAntecedenciaServico
        ) {
          mensagemLembrete = await enviarMensagemPorContexto(
            agendamentoTemp,
            "lembreteAgendamentoServico",
          );
        }
        const body = {
          ...agendamentoTemp,
          lembreteAntecedencia:
            empresa?.configuracoes?.lembreteAntecedenciaServico,
          mensagemLembrete,
        };
        const retornoAlerta = await notification.confirmacao(
          "Confirmação",
          `Deseja confirmar o agendamento para ${format(
            formatDate.received(agendamentoTemp.periodo),
            "dd/MM/yyyy 'às' HH:mm?",
          )}`,
        );
        if (retornoAlerta.isConfirmed) {
          const response = await agendamentoServicosService.cadastrar(body);
          if (!response.isAxiosError) {
            notification.cadastroSucesso();
            await enviarConfirmacaoAgendamento(agendamentoTemp);
            await atualizarStatus(data?.id, "AGENDADO");
            if (!response.data?.mensagemEnviada) {
              notification.alertaGenericos(
                "Lembrete não pode ser enviado. Favor verificar a conexão!",
              );
            }
            onCloseDialog();
          } else {
            responseErros(response);
          }
        }
      })
      .catch((err) => {
        if (err.isAxiosError) {
          responseErros(err);
        } else {
          inputErros.set(err);
        }
      })
      .finally(() => setLoading(false));
  };

  async function enviarConfirmacaoAgendamento(agendamento) {
    const mensagem = await enviarMensagemPorContexto(
      agendamento,
      "agendamentoServico",
    );

    await adzapService.enviarMensagem({
      number: agendamento.celular,
      type: "text",
      text: mensagem,
    });
  }

  async function enviarMensagemPorContexto(agendamento, contexto) {
    const getPrimeiroNome = (nomeCompleto) => nomeCompleto?.split(" ")[0] || "";

    const texto = await mensagemPersonalizadaService.getMensagem({
      contexto,
      apelidoFantasia: empresaUsuario.empresa?.fantasia,
      nomeCompletoCliente: agendamento.clienteNome,
      primeiroNomeCliente: getPrimeiroNome(agendamento.clienteNome),
      nomeUsuario: empresaUsuario?.usuario?.nome,
      dataAgendamento: format(new Date(agendamento.periodo), "dd/MM/yyyy"),
      horaAgendamento: format(new Date(agendamento.periodo), "HH:mm"),
    });

    if (texto.isAxiosError) {
      throw texto;
    }

    return texto.data;
  }

  const handleChangeTextfield = (event) => {
    setAgendamento((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperComponent="div"
      PaperProps={{
        style: {
          borderRadius: "0.7rem",
          background: "white",
        },
      }}
      open={open}
      onClose={() => onCloseDialog()}
    >
      <DialogContent>
        <h6 className="text-uppercase fw-bold pt-2 mx-3">
          AGENDAMENTO DE SERVIÇO
        </h6>
        <CardContent>
          <AgendamentoServicoForm
            agendamento={agendamento}
            setAgendamento={setAgendamento}
            configuracoes={configuracoes}
            servico={servico}
            servicoList={servicoList}
            clienteList={clienteList}
            prestadorServicoList={prestadorServicoList}
            horariosList={horariosList}
            handleChangeTextfield={handleChangeTextfield}
            onChangeDate={onChangeDate}
            onChangePrestador={onChangePrestador}
          />
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                color="padrao"
                onClick={() => onCloseDialog()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                FECHAR
              </Button>
            </Grid>
            <Grid item xs={9}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                CONFIRMAR AGENDAMENTO
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

export default AgendamentoServicoDialog;
